import { ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, ViewChild } from "@angular/core";
import { PdfService } from "../model-services/pdf.service";
import { GraphComponent } from "@yfiles/yfiles";
import { assetInputs } from "../information/information-analysis-model";
import { Currency, DEFAULT_CURRENCY, getCurrency, getModelsForFilter, InsightsFilter, ModelDetails, Organisation, Project, totalModelInsights, getValueFromModelsInSet, defaultCapacityHours } from "../common/organisation";
import { CURRENT_STATE_PROJECT_KEY, GraphData, LinqsetNode, NODE_SUPERTYPE, NODE_TYPE, ORGANISATION_MODEL_KEY, OUTPUT_VALUE_SOURCE, SYSTEM_KIND, WORK_TYPE } from "../common/linqset-model";
import { AnalysisEdge, AnalysisNode } from "../common/analysis-model";
import { OrganisationService } from "../organisation/organisation.service";
import { FormattingService } from "../linq-utils/formatting/formatting.service";
import { InsightsService } from "../model-services/insights.service";
import { MODEL_API, RepositoryService } from "../linq-utils/repository.service";
import { buildEpmScorecard, elapsed, countDecisionStatuses, decisionsFromAI, decisionStatus, DimensionSummary, EpmDecision, epmComponents, EpmDecisionStatus, EpmDetourRow, EpmInformationRow, EpmMeasure, EpmScorecard } from "./epm-scorecard-model";
import { AIServices } from "../ai/ai-services";
import { AIEpmValueSuggestion } from "../common/epm-decisions";
import { USER_ROLE } from "../common/user";
import { AnalysisScope, scopeFromFilter, wholeOrganisation } from "../analysis/scope";
import { ContextRequest, ContextSource } from "../analysis/context-graph.service";
import { subjectForDetour } from "../analysis/context-graph";
import { NodeCorrection, NodeCorrectionService } from "../analysis/node-correction.service";
import { SystemKindChoice } from "../analysis/node-fixes.component";
import { systemKindOf } from "../common/system-kind";
import { ValueProfileBar, valueProfileChart } from "./value-profile";

type Lens = 'friction' | 'risk' | 'information';
type ChangeMode = 'both' | 'absolute' | 'percent';

export interface ChangeChip {
    text: string;
    tone: 'good' | 'bad' | 'flat';
}

const COMPONENT = "EPM scorecard"
const PDF_SECTION_GAP = 3       // mm between sections in the PDF

// AI names outputs as the evidence gave them, which is by name and model
const sameName = (a:string|null|undefined, b:string|null|undefined) => !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase()
const PDF_SCALE = 1.5           // capture resolution: sharp enough to read when printed, small enough to stay fast

@Component({
    selector: 'app-epm-scorecard',
    templateUrl: './scorecard.html',
    styleUrls: ['./scorecard.css', './scorecard-features.css'],
    standalone: false
})
export class ScorecardComponent implements OnChanges, OnDestroy {
    @Input() models:ModelDetails[] = []
    @Input() graphComponent:GraphComponent
    // the actions, outputs and their connections, when they come from analysis data rather than a graph (as in an insights tab)
    @Input() analysisNodes?:AnalysisNode[]
    @Input() analysisEdges?:AnalysisEdge[]
    @Input() organisationModelData:GraphData       // organisation model, for its roles and systems
    @Input() filter:InsightsFilter                 // the models shown, used to score the same models in a future state
    @Input() workspaceKey:string = CURRENT_STATE_PROJECT_KEY
    @Input() workspaceName:string = "Current state"
    @Input() refreshTrigger:any                    // refresh trigger
    @Input() active = false                        // whether the scorecard is showing. AI decisions are only generated when it is
    @Input() dimensionSummary?:DimensionSummary    // the heatmap dimension chosen beside the graph, if any
    @Input() readOnly = false                      // for viewing only, as in the model introduction: no AI to ask and no links away to edit
    @Output() stepSelected = new EventEmitter<string>()   // the key of a step chosen to be shown in the graph
    @Output() valuesApplied = new EventEmitter<void>()    // output values were set from the scorecard and the insights rescored, so the graph is stale

    organisation:Organisation
    currency:Currency
    current:EpmScorecard                           // the scorecard for the graph shown
    future:EpmScorecard                            // the same models in the chosen future state
    baseline?:EpmScorecard                         // in a project, the same models in the current state, which its figures are compared with
    private baselineRequest = 0
    futureProject:Project
    loadingFuture = false
    futureError = ""

    state:'current'|'future' = 'current'
    changeMode:ChangeMode = 'both'
    lens:Lens = 'friction'

    // decisions are generated by AI from the scorecard's evidence, falling back to the rule-based decisions
    aiDecisions:EpmDecision[]|undefined
    aiState:'idle'|'loading'|'ready'|'failed' = 'idle'
    private aiCache = new Map<string, EpmDecision[]>()     // by evidence, so reopening the same scorecard doesn't regenerate
    private aiRequest = 0

    // values per item AI suggests for the outputs, from the scorecard's evidence, which the user can set on the outputs
    valueSuggestions:AIEpmValueSuggestion[]|undefined
    valueState:'idle'|'loading'|'ready'|'failed' = 'idle'
    valueError = ""
    applyingValues = false
    private valueRequest = 0

    // a node a finding rests on, being changed from the scorecard
    correcting = false
    correctionError = ""


    // PDF export: while exporting, the scorecard lays out at a fixed width and shows all three lenses
    @ViewChild('scorecardRoot') scorecardRoot:ElementRef<HTMLElement>
    exporting = false
    exportDate = ""
    exportError = ""

    // the models the scorecard shows, in the workspace it shows, for the context graphs of its rows
    get contextScope():AnalysisScope {
        const scope = this.filter ? scopeFromFilter(this.filter) : wholeOrganisation(this.workspaceKey)
        return this.state === 'future' && this.futureProject ? {...scope, workspaceKey: this.futureProject._key} : scope
    }

    // the graph already in hand (for the current state), so a context opens without loading
    get contextSource():ContextSource|undefined {
        return this.state === 'current' && this.analysisNodes ? {graph: {nodes: this.analysisNodes, edges: this.analysisEdges || []}, organisationModel: this.organisationModelData} : undefined
    }

    readonly detourSubject = subjectForDetour

    // what a decision's context graph shows: for a risk it addresses (a bridge or a local tool), what that risk's graph
    // shows; otherwise the node of the first signal it addresses, or failing that the first of the nodes it names that
    // the graph has. Nothing for a decision about neither. Kept for each decision, so the button isn't given a new
    // request each check
    private decisionContexts = new WeakMap<EpmDecision, ContextRequest|null>()
    decisionContext(d:EpmDecision):ContextRequest|undefined {
        let request = this.decisionContexts.get(d)
        if (request === undefined) {
            const risk = d.subjects.map(s => this.scorecard?.informationRisks.find(r => r.key === s.key)).find(r => !!r)
            request = risk ? {subject: {kind: risk.context.kind, key: risk.context.key, name: d.title}}
                : d.nodes.length ? {recommendation: {focus: d.subjects.find(s => s.key !== 'all')?.key, subjects: d.nodes.map(n => n.name), title: d.title}}
                : null
            this.decisionContexts.set(d, request)
        }
        return request || undefined
    }
    readonly WORK_TYPE = WORK_TYPE
    readonly ORGANISATION_MODEL_KEY = ORGANISATION_MODEL_KEY

    // an amount in the organisation's currency, shortened, for the tables
    money(value:number) {
        return value > 0 ? this.formatter.formatAbbreviatedNumber(value, this.currency) : '-'
    }

    // the manual bridges between systems that add to friction (their steps not adding value), which the friction lens
    // shows for that time; every bridge is under Risk
    get bridgeRows() {
        return this.scorecard?.informationRisks?.filter(r => r.kind === 'bridge' && (r.frictionHours || 0) > 0) || []
    }

    constructor(private organisationService:OrganisationService, private formatter:FormattingService, private aiServices:AIServices,
        private insightsService:InsightsService, private repoService:RepositoryService, private pdfService:PdfService,
        private changeDetectorRef:ChangeDetectorRef, private corrections:NodeCorrectionService) {}

    // ---------- correcting the nodes a finding rests on ----------

    // nodes can be changed from the scorecard in current state, by those who can change it
    get canCorrect():boolean {
        return this.state === 'current' && this.corrections.canCorrect(this.organisation, this.workspaceKey)
    }

    // the links beside a finding: its context graph and opening it in its model
    get showLinks():boolean {
        return !this.exporting && !this.readOnly && !!this.organisation
    }

    // the node in its model, selected to edit, in the workspace shown
    editInModel(modelKey:string, key:string) {
        if (this.organisation && modelKey && key) this.corrections.openInModel(this.organisation, modelKey, key, this.contextScope.workspaceKey)
    }

    // the systems a finding rests on, with their kind as the user said or as LINQ reads it from the name
    systemChoices(keys:string[]):SystemKindChoice[] {
        const nodes = new Map((this.organisationModelData?.nodes || []).map(n => [n._key, n]))
        return keys.map(k => nodes.get(k)).filter((n): n is LinqsetNode => !!n).map(n => ({key: n._key, name: n.name, ...systemKindOf(n)}))
    }

    // the kind of work the actions on a detour are, as the user says
    setWorkType(row:EpmDetourRow, workType:WORK_TYPE) {
        void this.correct(row.stepKeys.map(key => ({modelKey: row.modelKey, key, properties: {workType}})), "Save the kind of work")
    }

    // the kind of work one action is, as the user says
    setStepWorkType(modelKey:string, key:string, workType:WORK_TYPE) {
        void this.correct([{modelKey, key, properties: {workType}}], "Save the kind of work")
    }

    setHeldAs(information:{key:string, modelKey:string}, type:NODE_TYPE) {
        void this.correct([{modelKey: information.modelKey, key: information.key, type}], "Save how the information is held")
    }

    setSystemKind(change:{key:string, kind:SYSTEM_KIND}) {
        void this.correct([{modelKey: ORGANISATION_MODEL_KEY, key: change.key, properties: {systemKind: change.kind}}], "Save the system's kind")
    }

    // a value typed in, so it is the user's own rather than a suggestion accepted
    setOutputValue(row:{key:string, modelKey:string}, value:number) {
        void this.correct([{modelKey: row.modelKey, key: row.key, properties: {outputValue: value, outputValueSource: null}}], "Save the output's value")
    }

    // saves the changes, has the insights worked out again, and tells the page the graph needs reloading
    private async correct(changes:NodeCorrection[], what:string) {
        if (!this.canCorrect || this.correcting || !changes.length) return
        this.correcting = true
        this.correctionError = ""
        try {
            await this.corrections.apply(this.organisation, changes, what)
            this.valuesApplied.emit()
        }catch(err) {
            console.error("Unable to correct the nodes", err)
            this.correctionError = "The change couldn't be saved. Please try again."
        }finally {
            this.correcting = false
        }
    }

    // ---------- full screen ----------

    // the scorecard fills the window, over the rest of the page, and the browser goes full screen where it allows it.
    // The whole page goes full screen, not just the scorecard, so dialogs and menus opened from it still show
    fullScreen = false
    private browserFullScreen = false

    async toggleFullScreen() {
        if (this.fullScreen) return this.leaveFullScreen()
        this.fullScreen = true
        try {
            if (!document.fullscreenElement && document.documentElement.requestFullscreen) {
                await document.documentElement.requestFullscreen()
                this.browserFullScreen = true
            }
        } catch {
            // not allowed (as in a frame): filling the window is enough
        }
    }

    leaveFullScreen() {
        this.fullScreen = false
        if (this.browserFullScreen && document.fullscreenElement) document.exitFullscreen?.().catch(() => undefined)
        this.browserFullScreen = false
    }

    // the browser left full screen (Esc, or its own control): so does the scorecard
    @HostListener('document:fullscreenchange')
    onFullScreenChange() {
        if (!document.fullscreenElement && this.browserFullScreen) {
            this.browserFullScreen = false
            this.fullScreen = false
            this.changeDetectorRef.markForCheck()
        }
    }

    // Esc leaves it when the browser isn't full screen, unless a dialog or menu is open, which Esc closes instead
    @HostListener('document:keydown.escape')
    onEscape() {
        if (this.fullScreen && !this.browserFullScreen && !document.querySelector('.cdk-overlay-pane')) this.leaveFullScreen()
    }

    ngOnDestroy() {
        if (this.fullScreen) this.leaveFullScreen()
    }

    // exports the scorecard as shown (current or future state) to an A4 PDF, a section at a time so sections aren't split
    async exportPdf() {
        if (this.exporting || !this.scorecard) return
        this.exporting = true
        this.exportError = ""
        this.exportDate = new Date().toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })
        this.changeDetectorRef.detectChanges()
        await new Promise(resolve => setTimeout(resolve, 50))           // let the export layout settle

        try {
            // capture the whole scorecard once (each capture copies the page, so one per section is very slow), then
            // add each section's band of the image, so a section is never split across a page break
            const slices = await this.pdfService.captureSections(this.scorecardRoot.nativeElement, PDF_SCALE,
                root => Array.from(root.querySelectorAll<HTMLElement>('[data-pdf-block]')))

            const pdf = await this.pdfService.createPDF('p')
            let bottom:number|undefined
            for (const slice of slices) {
                bottom = this.pdfService.addCanvasToPdf(pdf, slice, bottom !== undefined ? bottom + PDF_SECTION_GAP : undefined)
            }
            pdf.save(this.pdfFileName())
        }catch(err) {
            console.error("Unable to export the scorecard", err)
            this.exportError = "The PDF couldn't be created. Please try again."
        }finally {
            this.exporting = false
            // the capture finishes outside Angular's change detection, so update the view (the button and layout) now
            this.changeDetectorRef.detectChanges()
        }
    }

    private pdfFileName() {
        const name = `${this.scorecard.scopeName} EPM scorecard - ${this.scorecard.workspaceName}`
        return name.replace(/[\\/:*?"<>|]/g, '').replace(/\s+/g, ' ').trim() + '.pdf'
    }

    get scorecard():EpmScorecard {
        return this.state === 'future' && this.future ? this.future : this.current
    }

    get projects():Project[] {
        return this.organisation?.projects || []
    }

    projectByKey(key:string) {
        return this.projects.find(p => p._key === key)
    }

    // the future state is judged by the decisions the current state points to, so it shows those
    get decisions():EpmDecision[] {
        return this.aiDecisions || this.current?.decisions || []
    }

    get decisionSource():'ai'|'rules' {
        return this.aiDecisions ? 'ai' : 'rules'
    }

    ngOnChanges(): void {
        this.organisation = this.organisationService.organisation
        if (!this.organisation || (!this.graphComponent && !this.analysisNodes) || !this.organisationModelData) return

        this.currency = getCurrency(this.organisation.currencyCode||DEFAULT_CURRENCY)
        const nodes = this.analysisNodes ?? this.graphComponent.graph.nodes.map(n => n.tag?.node as AnalysisNode).filter(n => !!n).toArray()
        const edges = this.analysisNodes ? (this.analysisEdges || []) : this.graphComponent.graph.edges.map(e => e.tag?.edge as AnalysisEdge).filter(e => !!e).toArray()
        this.current = this.createScorecard(nodes, edges, this.organisationModelData, this.models, this.workspaceKey, this.workspaceName)
        this.aiDecisions = this.aiCache.get(this.evidenceKey())
        this.aiState = this.aiDecisions ? 'ready' : 'idle'
        if (this.active) this.generateDecisions()

        // the graph changed, so rescore the future state for the same models
        if (this.futureProject) this.loadFuture(this.futureProject)
        // a project is compared with the current state
        if (this.workspaceKey !== CURRENT_STATE_PROJECT_KEY) this.loadBaseline()
        else this.baseline = undefined
    }

    // the same models (by the same filter) in the current state, for a project's figures to be compared with
    private async loadBaseline() {
        const request = ++this.baselineRequest
        try {
            const filter:InsightsFilter = { ...(this.filter||{} as InsightsFilter), workspaceKey: CURRENT_STATE_PROJECT_KEY,
                modelKeys: this.filter?.modelKeys||[], modelFunctions: this.filter?.modelFunctions||[], modelTags: this.filter?.modelTags||[] }
            const models = getModelsForFilter(filter, this.organisation)
            const modelKeys = models.map(m => m._key)
            const [data, organisationModel] = await Promise.all([
                this.insightsService.loadAnalysisGraphForFilter(this.organisation, filter),
                this.repoService.callSingleAPI(this.repoService.getData<GraphData>(MODEL_API(this.organisation._key, ORGANISATION_MODEL_KEY, CURRENT_STATE_PROJECT_KEY)), COMPONENT, "Load the current state organisation model"),
            ])
            if (request !== this.baselineRequest) return
            this.baseline = this.createScorecard(data.nodes.filter(n => modelKeys.includes(n.modelKey)), data.edges.filter(e => modelKeys.includes(e.modelKey)),
                organisationModel, models, CURRENT_STATE_PROJECT_KEY, "Current state")
        }catch(err) {
            console.error("Unable to load the current state to compare with", err)
            if (request === this.baselineRequest) this.baseline = undefined
        }
        this.changeDetectorRef.detectChanges()
    }

    private createScorecard(nodes:AnalysisNode[], edges:AnalysisEdge[], organisationModel:GraphData, models:ModelDetails[], workspaceKey:string, workspaceName:string) {
        const modelKeys = [...new Set(nodes.map(n => n.modelKey))]
        const scoped = models.filter(m => modelKeys.includes(m._key))
        const totals = scoped.length > 0 ? totalModelInsights(scoped, workspaceKey, this.organisation._key) : undefined

        return buildEpmScorecard({
            scopeName: scoped.length === 1 ? scoped[0].name : `The organisation (${scoped.length} models)`,
            workspaceName,
            modelKeys,
            nodes,
            edges,
            components: epmComponents(organisationModel, workspaceKey),
            modelNames: Object.fromEntries(this.organisation.models.map(m => [m._key, m.name])),
            defaultCapacity: defaultCapacityHours(this.organisation) > 0,
            assets: assetInputs(this.organisation.informationAssets, totals, organisationModel.nodes),
            totals: { revenue: totals?.outputRevenue||0, value: totals?.outputValue||0 },
            modelValues: Object.fromEntries(scoped.map(m => {
                // what it receives from the other models shown is counted in theirs, as the totals count it
                const insights = m.insights?.find(i => i.projectKey === workspaceKey)
                const fromSet = getValueFromModelsInSet(insights, modelKeys)
                return [m._key, { revenue: insights ? insights.outputRevenue - fromSet.revenue : 0, value: insights ? insights.outputValue - fromSet.value : 0 }]
            })),
            currency: this.currency.code,
            money: v => this.formatMoney(v),
            number: v => this.formatter.formatAbbreviatedNumber(v),
        })
    }

    // ---------- AI decisions ----------

    private evidenceKey() {
        return this.current ? JSON.stringify(this.current.evidence) : ''
    }

    // generates decisions with AI for the current scorecard, unless already generated for the same evidence
    async generateDecisions(regenerate = false) {
        if (!this.current || this.aiState === 'loading') return
        const key = this.evidenceKey()
        if (!regenerate && this.aiCache.has(key)) {
            this.aiDecisions = this.aiCache.get(key)
            this.aiState = 'ready'
            return
        }

        const request = ++this.aiRequest
        this.aiState = 'loading'
        try {
            const generated = await this.aiServices.generateEpmDecisions(this.organisation._key, this.workspaceKey, this.current.evidence)
            if (request !== this.aiRequest) return          // the scorecard changed while waiting
            const decisions = decisionsFromAI(generated, this.current.evidence)
            if (decisions.length === 0) throw new Error("AI returned no usable decisions")
            this.aiCache.set(key, decisions)
            this.aiDecisions = decisions
            this.aiState = 'ready'
        }catch(err) {
            if (request !== this.aiRequest) return
            console.error("Unable to generate EPM decisions with AI, using the rule-based decisions", err)
            this.aiDecisions = undefined
            this.aiState = 'failed'
        }
    }

    // ---------- output values ----------

    // values can only be set on current state, by those who can change it, and from the current state's rows
    get canApplyValues():boolean {
        return this.workspaceKey === CURRENT_STATE_PROJECT_KEY && !!this.organisation && (this.organisation.role || USER_ROLE.READ) <= USER_ROLE.WRITE && this.state === 'current'
    }

    // none for an output joined into another model, which takes its value from there
    suggestionFor(row:EpmInformationRow):AIEpmValueSuggestion|undefined {
        if (row.joinedOut) return undefined
        return this.valueSuggestions?.find(s => sameName(s.name, row.name) && (!s.model || sameName(s.model, row.model)))
    }

    // the rows whose suggested value differs from the value set on the output
    get pendingSuggestions():{row:EpmInformationRow, suggestion:AIEpmValueSuggestion}[] {
        return (this.current?.informationRows || [])
            .map(row => ({row, suggestion: this.suggestionFor(row) as AIEpmValueSuggestion}))
            .filter(p => p.suggestion && p.suggestion.value !== p.row.userValue)
    }

    // asks AI for a value per item for each output in the scorecard's evidence
    async suggestValues(regenerate = false) {
        if (!this.current || this.valueState === 'loading' || (this.valueState === 'ready' && !regenerate)) return
        const request = ++this.valueRequest
        this.valueState = 'loading'
        this.valueError = ""
        try {
            const suggestions = await this.aiServices.suggestEpmValues(this.organisation._key, this.workspaceKey, this.current.evidence)
            if (request !== this.valueRequest) return
            if (suggestions.length === 0) throw new Error("AI returned no usable value suggestions")
            this.valueSuggestions = suggestions
            this.valueState = 'ready'
        }catch(err) {
            if (request !== this.valueRequest) return
            console.error("Unable to suggest output values with AI", err)
            this.valueSuggestions = undefined
            this.valueState = 'failed'
        }
    }

    applySuggestion(row:EpmInformationRow) {
        const suggestion = this.suggestionFor(row)
        if (!suggestion) return
        void this.applyValues([{row, value: suggestion.value, source: OUTPUT_VALUE_SOURCE.AI}])
    }

    applyAllSuggestions() {
        void this.applyValues(this.pendingSuggestions.map(p => ({row: p.row, value: p.suggestion.value, source: OUTPUT_VALUE_SOURCE.AI})))
    }

    useDerivedValue(row:EpmInformationRow) {
        void this.applyValues([{row, value: row.derivedValue, source: OUTPUT_VALUE_SOURCE.DERIVED}])
    }

    // sets the values on the outputs in their current state models, has the insights worked out again, and tells the
    // page the graph needs reloading
    private async applyValues(values:{row:EpmInformationRow, value:number, source:OUTPUT_VALUE_SOURCE}[]) {
        if (!this.canApplyValues || this.applyingValues || values.length === 0) return
        this.applyingValues = true
        this.valueError = ""
        try {
            await this.corrections.apply(this.organisation, values.map(({row, value, source}) =>
                ({modelKey: row.modelKey, key: row.key, properties: {outputValue: value, outputValueSource: source}})), "Save the output values")
            this.valuesApplied.emit()
        }catch(err) {
            console.error("Unable to set the output values", err)
            this.valueError = "The values couldn't be saved. Please try again."
        }finally {
            this.applyingValues = false
        }
    }

    // ---------- future state ----------

    async selectFuture(project:Project) {
        this.futureProject = project
        await this.loadFuture(project)
        if (this.future) this.state = 'future'
    }

    showState(state:'current'|'future') {
        this.state = state
    }

    // scores the same models (by the same filter) in the project workspace
    private async loadFuture(project:Project) {
        this.loadingFuture = true
        this.futureError = ""
        try {
            const filter:InsightsFilter = { ...(this.filter||{} as InsightsFilter), workspaceKey: project._key,
                modelKeys: this.filter?.modelKeys||[], modelFunctions: this.filter?.modelFunctions||[], modelTags: this.filter?.modelTags||[] }
            const models = getModelsForFilter(filter, this.organisation, project)
            const modelKeys = models.map(m => m._key)

            const [data, organisationModel] = await Promise.all([
                this.insightsService.loadAnalysisGraphForFilter(this.organisation, filter),
                this.repoService.callSingleAPI(this.repoService.getData<GraphData>(MODEL_API(this.organisation._key, ORGANISATION_MODEL_KEY, project._key)), COMPONENT, "Load future state organisation model"),
            ])
            const nodes = data.nodes.filter(n => modelKeys.includes(n.modelKey))
            const edges = data.edges.filter(e => modelKeys.includes(e.modelKey))
            this.future = this.createScorecard(nodes, edges, organisationModel, models, project._key, project.name)
        }catch(err) {
            console.error("Unable to load the future state", err)
            this.future = undefined
            this.futureError = `Unable to load ${project.name}`
            this.state = 'current'
        }finally {
            this.loadingFuture = false
        }
    }

    // ---------- comparison ----------

    // what the figures shown are compared with: the scorecard shown first, for a future state; in a project, the
    // current state
    get reference():EpmScorecard|undefined {
        return this.state === 'future' ? (this.future ? this.current : undefined) : this.baseline
    }

    // change against the reference, shown beside a figure. Figures with different units aren't compared
    change(current:EpmMeasure|undefined, future:EpmMeasure|undefined):ChangeChip|undefined {
        if (!this.reference || !future) return undefined
        if (!current) return { text: 'new', tone: 'flat' }
        if (current.unit !== future.unit) return undefined

        const diff = future.amount - current.amount
        if (future.unit === 'time' ? Math.abs(diff) < 1/60 : Math.round(diff) === 0) return { text: 'no change', tone: 'flat' }
        const better = future.better === 'up' ? diff > 0 : diff < 0
        const arrow = diff < 0 ? '▼' : '▲'
        const absolute = this.formatChange(Math.abs(diff), future.unit)
        const percent = current.amount !== 0 ? `${Math.round(Math.abs(diff) / Math.abs(current.amount) * 100)}%` : 'new'
        const text = this.changeMode === 'absolute' ? `${arrow} ${absolute}`
            : this.changeMode === 'percent' ? `${arrow} ${percent}`
            : `${arrow} ${absolute} (${percent})`
        return { text, tone: better ? 'good' : 'bad' }
    }

    headlineChange(label:string, future:EpmMeasure|undefined) {
        return this.change(this.reference?.headlines.find(h => h.label === label)?.measure, future)
    }

    panelChange(section:'profitAndLoss'|'balanceSheet', title:string, label:string, future:EpmMeasure|undefined) {
        const line = this.reference?.[section].find(p => p.title === title)?.lines.find(l => l.label === label)
        return this.change(line?.measure, future)
    }

    ledgerChange(side:'value'|'drag', label:string, future:EpmMeasure|undefined) {
        return this.change(this.reference?.ledger[side].find(l => l.label === label)?.measure, future)
    }

    // the future state is judged against the decisions the current state points to
    status(decision:EpmDecision):EpmDecisionStatus|undefined {
        return this.future ? decisionStatus(decision, this.current, this.future) : undefined
    }

    statusText(decision:EpmDecision):string {
        const text:{[status in EpmDecisionStatus]:string} = { resolved: 'resolved', improved: 'improved', open: 'still open', untracked: 'not tracked' }
        const status = this.status(decision)
        return status ? text[status] : ''
    }

    // how many of the current state's decisions the future state resolves, improves or leaves open
    get statusCounts() {
        return this.future && this.current ? countDecisionStatuses(this.decisions, this.current, this.future) : undefined
    }

    // describes where the decisions being judged came from
    get decisionSourceLabel():string {
        return this.decisionSource === 'ai' ? 'AI-generated decisions' : 'rule-based decisions'
    }

    // a short comparison of the headlines, for the future-state summary
    get comparisonSummary():string {
        if (!this.future || !this.current) return ""
        const parts = this.future.headlines.map(h => {
            const before = this.current.headlines.find(c => c.label === h.label)
            return before && before.value !== h.value ? `${h.label.toLowerCase()} from <b>${before.value}</b> to <b>${h.value}</b>` : undefined
        }).filter(p => !!p)
        const decisions = this.decisions.filter(d => !d.gate)
        const counts = this.statusCounts!
        const partly = counts.improved ? `, improves <b>${counts.improved}</b>` : ''
        return `Compared with ${this.current.workspaceName.toLowerCase()}, ${parts.length ? parts.join(', ') : 'the headline figures are unchanged'}. ` +
            `Of the ${decisions.length} ${this.decisionSourceLabel} for ${this.current.workspaceName.toLowerCase()}, it resolves <b>${counts.resolved}</b>${partly} and leaves <b>${counts.open}</b> open.`
    }

    // a project's headline figures against the current state's
    get baselineSummary():string {
        if (!this.baseline || !this.current) return ""
        const parts = this.current.headlines.map(h => {
            const before = this.baseline!.headlines.find(c => c.label === h.label)
            return before && before.value !== h.value ? `${h.label.toLowerCase()} from <b>${before.value}</b> to <b>${h.value}</b>` : undefined
        }).filter(p => !!p)
        return `Compared with the current state, ${parts.length ? parts.join(', ') : 'the headline figures are unchanged'}.`
    }

    private formatChange(value:number, unit:EpmMeasure['unit']) {
        switch (unit) {
            case 'money': return this.formatMoney(value)
            case 'hours': return `${this.formatter.formatAbbreviatedNumber(value)} hrs`
            case 'time': return elapsed(value)
            case 'percent': return `${Math.round(value)} pts`
            default: return this.formatter.formatAbbreviatedNumber(value)
        }
    }

    // the shared formatter doesn't handle negative numbers, which a net value can be
    private formatMoney(value:number) {
        const formatted = this.formatter.formatAbbreviatedNumber(Math.abs(value), this.currency)
        return value < 0 ? "-" + formatted : formatted
    }

    // the value profile's bars laid out, worked out again only when the scorecard shown changes
    private profileFor?:ValueProfileBar[]
    private profile?:ReturnType<typeof valueProfileChart>
    valueProfileChart(bars:ValueProfileBar[]) {
        if (bars !== this.profileFor) {
            this.profileFor = bars
            this.profile = valueProfileChart(bars)
        }
        return this.profile!
    }

    // a model's figures, on hovering over its bar
    valueProfileTip(bar:ValueProfileBar) {
        const lines = [`${bar.name}: ${this.valueProfileAmount(bar)} a year`,
            `Revenue ${this.formatMoney(bar.revenue)}, value ${this.formatter.formatAbbreviatedNumber(bar.value)}, cost of its people and systems ${this.formatMoney(bar.cost)}`]
        if (bar.unrealised > 0) lines.push(`Unrealised: outputs in use that no value reaches, which cost ${this.formatMoney(bar.unrealised)} to produce`)
        return lines.join('\n')
    }

    // what a model creates or destroys, signed
    valueProfileAmount(bar:ValueProfileBar) {
        return (bar.net < 0 ? '−' : '+') + this.formatMoney(Math.abs(bar.net))
    }

    gaugeStyle(load:number) {
        const filled = Math.min(100, Math.round(load / 2))         // the gauge shows 0 to 200%
        const colour = load > 100 ? 'var(--red)' : load > 85 ? 'var(--amber)' : 'var(--green)'
        return `background: conic-gradient(${colour} 0 ${filled}%, #eceef4 ${filled}% 100%)`
    }
}
